import type { RoundSubPhaseType } from '@repo/db'
import {
  formatGameRoster,
  formatGameRosterGroups,
  readRoster,
  readSettingMode,
  type GameRosterRole,
  type ParsedGameRoster,
} from '@repo/sdk'
import { ResponseOption } from './types'

export function isNumericOption(option: ResponseOption) {
  return option.possibleValues.length > 0 && option.possibleValues.every(v => typeof v === 'number')
}

export function isStringEnumOption(option: ResponseOption) {
  return option.possibleValues.length > 0
    && option.possibleValues.every(v => typeof v === 'string' && v !== '*')
}

export const SUBPHASE_I18N_KEYS: Record<RoundSubPhaseType, string> = {
  round_night_start: 'subPhases.round_night_start',
  round_magician: 'subPhases.round_magician',
  round_black_market_dealer: 'subPhases.round_black_market_dealer',
  round_gargoyle: 'subPhases.round_gargoyle',
  round_nightmare: 'subPhases.round_nightmare',
  round_dreamer: 'subPhases.round_dreamer',
  round_mechanical_wolf: 'subPhases.round_mechanical_wolf',
  round_mechanical_wolf_chase: 'subPhases.round_mechanical_wolf_chase',
  round_psychic: 'subPhases.round_psychic',
  round_wolf_younger: 'subPhases.round_wolf_younger',
  round_werewolf_discuss: 'subPhases.round_werewolf_discuss',
  round_werewolf_discuss_2: 'subPhases.round_werewolf_discuss_2',
  round_werewolf_kill: 'subPhases.round_werewolf_kill',
  round_wolf_beauty_charm: 'subPhases.round_wolf_beauty_charm',
  round_guard: 'subPhases.round_guard',
  round_witch_action: 'subPhases.round_witch_action',
  round_seer_verify: 'subPhases.round_seer_verify',
  round_hunter_action: 'subPhases.round_hunter_action',
  round_knight: 'subPhases.round_knight',
  round_gravekeeper: 'subPhases.round_gravekeeper',
  round_demon_hunter: 'subPhases.round_demon_hunter',
  round_lucky_one: 'subPhases.round_lucky_one',
  round_night_complete: 'subPhases.round_night_complete',
  round_day_start: 'subPhases.round_day_start',
  round_round_speech: 'subPhases.round_round_speech',
  round_vote: 'subPhases.round_vote',
  round_tied_round_speech: 'subPhases.round_tied_round_speech',
  round_tied_vote: 'subPhases.round_tied_vote',
  round_day_complete: 'subPhases.round_day_complete',
  round_sheriff_nominate: 'subPhases.round_sheriff_nominate',
  round_sheriff_campaign: 'subPhases.round_sheriff_campaign',
  round_sheriff_vote: 'subPhases.round_sheriff_vote',
  round_sheriff_tied_campaign: 'subPhases.round_sheriff_tied_campaign',
  round_sheriff_tied_vote: 'subPhases.round_sheriff_tied_vote',
}

const SUBPHASE_DAY_NIGHT: Record<RoundSubPhaseType, 'night' | 'day'> = {
  round_night_start: 'night',
  round_magician: 'night',
  round_black_market_dealer: 'night',
  round_gargoyle: 'night',
  round_nightmare: 'night',
  round_dreamer: 'night',
  round_mechanical_wolf: 'night',
  round_mechanical_wolf_chase: 'night',
  round_psychic: 'night',
  round_wolf_younger: 'night',
  round_werewolf_discuss: 'night',
  round_werewolf_discuss_2: 'night',
  round_werewolf_kill: 'night',
  round_wolf_beauty_charm: 'night',
  round_guard: 'night',
  round_witch_action: 'night',
  round_seer_verify: 'night',
  round_hunter_action: 'night',
  round_knight: 'night',
  round_gravekeeper: 'night',
  round_demon_hunter: 'night',
  round_lucky_one: 'night',
  round_night_complete: 'night',
  round_day_start: 'day',
  round_round_speech: 'day',
  round_vote: 'day',
  round_tied_round_speech: 'day',
  round_tied_vote: 'day',
  round_day_complete: 'day',
  round_sheriff_nominate: 'night',
  round_sheriff_campaign: 'night',
  round_sheriff_vote: 'night',
  round_sheriff_tied_campaign: 'night',
  round_sheriff_tied_vote: 'night',
}

export function isNightSubPhase(subPhase: RoundSubPhaseType | undefined | null): boolean {
  if (!subPhase)
    return false
  return SUBPHASE_DAY_NIGHT[subPhase] === 'night'
}

export type RoleConfig = GameRosterRole
export type ParsedGameSetting = ParsedGameRoster

export function parseGameSettingToPlayerConfig(gameSetting: string): ParsedGameSetting | null {
  return readRoster({ kind: 'selection', setting: gameSetting }).display
}

export function formatGameSettingDisplay(
  parsed: ParsedGameSetting,
  t: (key: string) => string,
  options: { includeEmoji?: boolean } = {},
): string {
  return formatGameRoster(parsed, roleKey => translatedRoleName(roleKey, t), options)
}

export function formatGameSettingGroups(
  parsed: ParsedGameSetting,
  t: (key: string) => string,
): { good: string, evil: string } {
  return formatGameRosterGroups(parsed, roleKey => translatedRoleName(roleKey, t))
}

function translatedRoleName(roleKey: string, t: (key: string) => string): string {
  for (const key of [`role.${roleKey}`, `game.${roleKey}`]) {
    const translated = t(key)
    if (translated && translated !== key)
      return translated
  }
  return roleKey.replaceAll('_', ' ')
}

export function formatRandomBoardDescription(t: (key: string) => string): string {
  return [
    t('game.g10RandomInfoIntro'),
    '',
    t('game.g10RandomInfoEvil'),
    t('game.g10RandomInfoGood'),
    t('game.g10RandomInfoNote'),
  ].join('\n')
}

export function formatGameSettingBoardLabel(
  gameSetting: string | undefined,
  _language: string | undefined,
  t: (key: string) => string,
): string | null {
  if (!gameSetting)
    return null

  const settingMode = readSettingMode(gameSetting)
  if (settingMode.terminalSuffix === 'R' || settingMode.markers.containsTrial)
    return formatRandomBoardDescription(t)

  const parsed = parseGameSettingToPlayerConfig(gameSetting)
  if (parsed)
    return formatGameSettingDisplay(parsed, t, { includeEmoji: true })

  const translationKey = `gameSettings.${gameSetting}`
  const translated = t(translationKey)
  return translated !== translationKey ? translated : gameSetting
}
